import { useEffect } from 'react';
import { useIssueAdjustmentsActions } from '@atlassian/jira-issue-adjustments';
import type { IssueConfiguration } from '@atlassian/jira-issue-field-base';
import {
	ChangeEventTypes,
	useIssueViewFieldUpdateEvents,
} from '@atlassian/jira-issue-view-field-update-events';
import { ISSUE_VIEW_VIEW_TYPE } from '@atlassian/jira-ui-modifications-constants';
import { isFieldTypeSupported } from '@atlassian/jira-ui-modifications-fields-configuration';
import { type changeSource, UI_MODIFICATIONS_CHANGE } from '../../../../common/constants';

type Props = {
	fieldsConfig: IssueConfiguration | null;
};

export const OnChangeHandlerRegistration = ({ fieldsConfig }: Props) => {
	const { processChange } = useIssueAdjustmentsActions();
	const [, { registerEventHandler }] = useIssueViewFieldUpdateEvents();

	useEffect(() => {
		const unregister = registerEventHandler('onChange', (event) => {
			if (
				event.type === ChangeEventTypes.FIELD_CHANGE_REQUESTED &&
				fieldsConfig &&
				fieldsConfig[event.meta.fieldId]
			) {
				const { schema } = fieldsConfig[event.meta.fieldId];
				const fieldType: string = schema.custom ?? schema.system;
				const isSupported = isFieldTypeSupported(ISSUE_VIEW_VIEW_TYPE, fieldType);
				const eventSourceIsUIM =
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(event.meta.additionalProperties as typeof changeSource)?.source ===
					UI_MODIFICATIONS_CHANGE;

				if (isSupported && !eventSourceIsUIM) {
					processChange({
						fieldId: event.meta.fieldId,
						fieldType,
						fieldValue: event.meta.fieldValue,
						eventType: 'change',
					});
				}
			}
		});

		return unregister;
	}, [fieldsConfig, registerEventHandler, processChange]);

	return null;
};
